import { useStore } from '@tanstack/react-form'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import Card from 'src/components/containers/card'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { CsvFileInput } from 'src/components/inputs/csv-file-input'
import BasicModal from 'src/components/modals/modal.basic'
import { uploadFileToCloud } from 'src/pages/files/uploadFileWorker'
import { StepId } from 'src/pages/uni-create/config'
import { processCsvFile } from 'src/utils/csv-parser'
import { useUniDataForFiles } from 'src/utils/hooks/use-get-uni-data-for-files-query'
import notify from 'src/utils/notify'

export const StepUploadFile: StepComponent = ({ context }) => {
  const form = context.form
  const navigate = useNavigate()
  const [nodeName, csvFile] = useStore(form.store, (state: any) => [state.values.nodeName, state.values.csvFile])
  const [showProgressModal, setShowProgressModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [uploadError, setUploadError] = useState(false)
  const [parseError, setParseError] = useState(false)
  const [uploadPercentage, setUploadPercentage] = useState(0)
  const [parseComplete, setParseComplete] = useState(false)
  const [uploadComplete, setUploadComplete] = useState(false)
  const uploadStarted = useRef(false)
  const parseStarted = useRef(false)
  const { currentNode, uploadFileTempCreds } = useUniDataForFiles(nodeName)

  // Reset state when modal is closed
  useEffect(() => {
    if (!showProgressModal) {
      uploadStarted.current = false
      parseStarted.current = false
      setUploading(false)
      setParsing(false)
      setUploadError(false)
      setParseError(false)
      setUploadComplete(false)
      setParseComplete(false)
    }
  }, [showProgressModal])

  // Handle concurrent upload and parsing when modal is shown
  useEffect(() => {
    if (!showProgressModal || !csvFile) return

    // Start parsing immediately (doesn't need credentials)
    if (!parseStarted.current) {
      parseStarted.current = true
      setParsing(true)
      setParseComplete(false)

      processCsvFile(csvFile, form)
        .then((result) => {
          if (result.success) {
            setParseComplete(true)
            setParsing(false)
          } else {
            setParseError(true)
            setParsing(false)
          }
        })
        .catch((err) => {
          console.error('Error parsing CSV:', err)
          setParseError(true)
          setParsing(false)
          notify.error('Error parsing CSV file. Please try again.')
        })
    }

    // Start upload when credentials are available
    if (!uploadFileTempCreds || !currentNode || uploadStarted.current) return

    const lastUploadedFileName = form.getFieldValue('lastUploadedCsvFileName')

    // Check if this file was already uploaded and parsed
    if (lastUploadedFileName === csvFile.name) {
      const parsedData = form.getFieldValue('parsedCsvData')
      const parsedFields = form.getFieldValue('parsedCsvFields')

      if (parsedData && parsedFields && parsedFields.length > 0) {
        // Already uploaded and parsed, just go to next step
        setShowProgressModal(false)
        context.goToStep(StepId.ConfigureCsv)
        return
      }
      // If not parsed, let it continue to re-parse (upload will be skipped below)
    }

    // Skip upload if file was already uploaded
    if (lastUploadedFileName === csvFile.name) {
      setUploadComplete(true)
      return
    }

    uploadStarted.current = true
    setUploading(true)
    setUploadError(false)
    setUploadPercentage(0)
    setUploadComplete(false)

    const destinationKey = csvFile.name
    const copyStrategyValue = 'NEVER'
    const readAccessValue = [nodeName].toString()
    const writeStrategyValue = [nodeName].toString()

    // Store the filename to avoid re-uploading if the user navigates back and forth
    form.setFieldValue('lastUploadedCsvFileName', csvFile.name)

    uploadFileToCloud(
      uploadFileTempCreds,
      currentNode,
      destinationKey,
      csvFile,
      copyStrategyValue,
      readAccessValue,
      writeStrategyValue,
      (progress) => {
        if (progress.loaded && progress.total) {
          setUploadPercentage(Math.round((progress.loaded / progress.total) * 100))
        }
      },
    )
      .then(() => {
        setUploading(false)
        setUploadPercentage(100)
        setUploadComplete(true)
      })
      .catch((err) => {
        console.error('Error uploading file:', err)
        setUploadError(true)
        setUploading(false)
        notify.error('Error uploading file. Please try again.')
      })
  }, [showProgressModal, csvFile, uploadFileTempCreds, currentNode, nodeName, form])

  // Navigate to next step when both operations complete successfully
  useEffect(() => {
    if (uploadComplete && parseComplete && showProgressModal) {
      setTimeout(() => {
        setShowProgressModal(false)
        context.goToStep(StepId.ConfigureCsv)
      }, 1000) // Brief delay to show completion
    }
  }, [uploadComplete, parseComplete, showProgressModal, context])

  return (
    <StepWrapper>
      <StepContentHeader centered large title='Upload CSV' description='Select a CSV file source for the new table' />
      <ScrollableStepContent inset autoHeight>
        <Card padding='xl' className='mx-8 w-full max-w-4xl'>
          <div className='flex items-center gap-5'>
            <img
              src='/images/vendor/upload-primary.png'
              alt='File Upload logo'
              className='size-14 rounded-lg border border-slate-200'
            />
            <div className='flex flex-col gap-0.5'>
              <div className='text-2xl font-bold'>CSV</div>
              <div className='text-neutral-8 text-sm'>Upload a CSV, up to 2GB in size.</div>
            </div>
          </div>
          <div className='my-10'>
            <form.Field name='csvFile'>
              {(field) => (
                <CsvFileInput
                  name='csvFile'
                  field={field}
                  maxFileSize={2 * 1024 * 1024 * 1024} // 2GB limit for lakehouse table creation uploads.
                  paddingYClass={'py-12'}
                  rules={{
                    required: 'This field is required',
                  }}
                />
              )}
            </form.Field>
          </div>
        </Card>
      </ScrollableStepContent>

      <BasicModal
        title='Processing CSV'
        isOpen={showProgressModal}
        onClose={() => {
          if (!uploading && !parsing) {
            setShowProgressModal(false)
          }
        }}
      >
        <div className='grid place-items-center gap-4'>
          <img className='mb-3' src='/images/sharing-files-illustration.svg' alt='Processing data' />

          {/* Upload Progress */}
          <div className='w-full'>
            <div className='mb-2 flex items-center justify-between'>
              <span className='text-sm font-semibold'>Uploading file</span>
              <span className='text-sm text-gray-500'>
                {uploadComplete ? '✓ Complete' : uploadError ? '✗ Failed' : `${uploadPercentage}%`}
              </span>
            </div>
            <progress
              className='[&::-webkit-progress-bar]:bg-uibg-2 [&::-moz-progress-bar]:bg-uibg-2 w-full [&::-webkit-progress-bar]:rounded-lg [&::-webkit-progress-value]:rounded-lg [&::-webkit-progress-value]:bg-purple-900 [&::-webkit-progress-value]:transition-all [&::-webkit-progress-value]:duration-1000 [&::-webkit-progress-value]:ease-out'
              max='100'
              value={uploadPercentage}
            >
              {uploadPercentage}
            </progress>
          </div>

          {/* Parsing Progress */}
          <div className='w-full'>
            <div className='mb-2 flex items-center justify-between'>
              <span className='text-sm font-semibold'>Analyzing structure</span>
              <span className='text-sm text-gray-500'>
                {parseComplete ? '✓ Complete' : parseError ? '✗ Failed' : parsing ? 'Processing...' : 'Waiting...'}
              </span>
            </div>
            <div className='h-2 w-full overflow-hidden rounded-lg bg-gray-200'>
              <div
                className={`h-full transition-all duration-500 ${
                  parseComplete ? 'w-full bg-purple-900' : parseError ? 'w-full bg-red-600' : 'w-0'
                }`}
              >
                {parsing && !parseComplete && !parseError && (
                  <div className='h-full w-full animate-pulse bg-purple-900 opacity-50'></div>
                )}
              </div>
            </div>
          </div>

          {/* Error Messages */}
          {uploadError || parseError ? (
            <div className='text-center'>
              <p className='font-bold text-red-600'>
                {uploadError && parseError ? 'Processing failed' : uploadError ? 'Upload failed' : 'Parsing failed'}
              </p>
              <p className='mb-4'>There was an error processing your file.</p>
              <button
                className='rounded bg-purple-900 px-4 py-2 text-white'
                onClick={() => setShowProgressModal(false)}
              >
                Close
              </button>
            </div>
          ) : (
            <p className='font-bold'>
              {uploadComplete && parseComplete ? 'Processing complete!' : 'Processing your CSV file...'}
            </p>
          )}
        </div>
      </BasicModal>

      <form.Subscribe selector={(state) => [state.values.csvFile]}>
        {([csvFile]) => (
          <StepButtons
            context={context}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            nextBlocked={!csvFile || uploading || parsing}
            onNext={() => {
              setUploadPercentage(0) // Reset percentage when starting new operation
              setShowProgressModal(true)
            }}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
